import { inject, computed, ref, watch, shallowRef, ComputedRef } from "vue";
import type { Ref } from "vue";
import type { ConfigurableWindow } from "@vueuse/core";
import { defaultWindow } from "@vueuse/core";
import {
  Connector,
  Providers,
  IProvider,
} from "@whitelabel-solutions/wallet-connector/";
import { ethers } from "ethers";
import makeBlockie from "ethereum-blockies-base64";
import { TypedDataUtils } from "ethers-eip712";
import { TypedData } from "ethers-eip712/dist/typed-data";
import { INFURA_ID } from "@/common/constants";
import getPrivatizedAddress from "@/common/filters/privatizeAddress";

const {
  MetaMaskProvider,
  WalletLinkProvider,
  WalletConnectProvider,
  FortmaticProvider,
  BinanceChainProvider,
} = Providers;

export const WALLET_CONTEXT = Symbol();

export interface Wallet {
  address: Ref<string>;
  balance: ComputedRef<ethers.BigNumber>;
  network: Ref<ethers.providers.Network | null>;
  avatarUri: Ref<string>;
  loading: Ref<boolean>;
  isConnected: ComputedRef<boolean>;
  privateAddress: Ref<string>;
  providers: IProvider[];

  connect: (provider: IProvider) => Promise<void>;
  disconnect: () => void;
  signTypedData: (typedData: TypedData) => Promise<string>;
  getProvider: () => ethers.providers.Web3Provider;
  getSigner: () => Promise<ethers.providers.JsonRpcSigner>;
}

const connector = Connector.init(
  {
    appName: "Whitelabel Market",
    infuraId: INFURA_ID,
    authereum: { key: "" },
    fortmatic: { key: "" },
  },
  [
    MetaMaskProvider,
    WalletLinkProvider,
    WalletConnectProvider,
    FortmaticProvider,
    BinanceChainProvider,
  ]
);

export function createWallet(options: ConfigurableWindow = {}): Wallet {
  const address = ref<string>("");
  const balance = computed<ethers.BigNumber>(() => {
    return ethers.BigNumber.from("0");
  });
  const network = ref<ethers.providers.Network | null>(null);
  const avatarUri = ref<string>("");
  const loading = ref<boolean>(false);
  const isConnected = computed<boolean>(() => !!_provider.value);
  const privateAddress = computed<string>(() =>
    getPrivatizedAddress(address.value)
  );
  const providers = connector.providers;

  const _provider = shallowRef<ethers.providers.Web3Provider | null>(null);

  const _getAddress = async (): Promise<string> => {
    const provider = getProvider();
    const accounts = await provider.listAccounts();
    return accounts[0];
  };

  const _getNetwork = async (): Promise<ethers.providers.Network> => {
    const provider = getProvider();
    return provider.getNetwork();
  };

  const getProvider = (): ethers.providers.Web3Provider => {
    if (!_provider.value) {
      throw Error("Wallet not connected");
    }
    return _provider.value;
  };

  const connect = async (instance: IProvider): Promise<void> => {
    loading.value = true;
    _provider.value = null;
    try {
      _provider.value = new ethers.providers.Web3Provider(
        await instance.connect()
      );
    } catch (e: any) {
      _provider.value = null;
      throw new Error(e.message);
    } finally {
      loading.value = false;
    }
  };

  const disconnect = (): void => {
    loading.value = true;
    _provider.value = null;
    network.value = null;
    address.value = "";
    loading.value = false;
  };

  const getSigner = async () => {
    const provider = getProvider();
    return provider.getSigner();
  };

  const signTypedData = async (typedData: TypedData): Promise<string> => {
    try {
      const signer = await getSigner();
      const digest = TypedDataUtils.encodeDigest(typedData);
      return signer.signMessage(digest);
    } catch (e: any) {
      throw new Error(e.message);
    }
  };

  const { window = defaultWindow } = options;

  // automatically detect wallet (only working for injected)
  if (window?.ethereum) {
    const ethereumProvider = new ethers.providers.Web3Provider(
      window.ethereum,
      "any"
    );

    window.ethereum.on("accountsChanged", async () => {
      console.log("accountsChanged");
    });

    ethereumProvider.on(
      "network",
      async (newNetwork: ethers.providers.Network) => {
        if (_provider.value) network.value = newNetwork;
      }
    );
  }

  // reload address and avatar on connect
  watch(_provider, async () => {
    if (isConnected.value) {
      loading.value = true;
      address.value = await _getAddress();
      network.value = await _getNetwork();
      avatarUri.value = makeBlockie(address.value);
      loading.value = false;
      console.log("Watched new Provider", wallet);
    }
  });

  const wallet: Wallet = {
    address,
    balance,
    network,
    avatarUri,
    loading,
    isConnected,
    privateAddress,
    providers,
    connect,
    disconnect,
    signTypedData,
    getProvider,
    getSigner,
  };

  return wallet;
}

export function useWallet(): Wallet {
  const context = inject(WALLET_CONTEXT) as Wallet;

  return context;
}
